import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Loader2,
  UtensilsCrossed,
} from 'lucide-react';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  publicMenuApi,
} from '../../api';
import PublicMenuExperience from '../../components/menu/PublicMenuExperience';
import {
  useLanguage,
} from '../../contexts/LanguageContext';
const PUBLIC_MENU_CART_PREFIX =
  'public_menu_cart_';
const PUBLIC_MENU_LIKES_PREFIX =
  'public_menu_likes_';
const PUBLIC_MENU_LANGUAGE_PREFIX =
  'public_menu_language_';
const toArray = (value) => {
  if (
    Array.isArray(value)
  ) {
    return value;
  }
  if (
    Array.isArray(
      value?.data,
    )
  ) {
    return value.data;
  }
  if (
    value &&
    typeof value ===
      'object'
  ) {
    return Object.values(
      value,
    );
  }
  return [];
};
const SUPPORTED_MENU_LANGUAGES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr'];

const getMenuLanguages = (menu) => {
  const raw = menu?.enabled_languages ?? menu?.selected_languages ??
    menu?.available_languages ?? menu?.settings?.enabled_languages ??
    menu?.settings?.selected_languages ?? menu?.settings?.available_languages;
  // Older menu API responses have no language list; retain all languages for compatibility.
  if (raw === undefined || raw === null || raw === '') return [...SUPPORTED_MENU_LANGUAGES];
  let parsed = raw;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); }
    catch { parsed = parsed.split(',').map((locale) => locale.trim()); }
  }
  if (parsed && !Array.isArray(parsed) && typeof parsed === 'object') {
    parsed = Object.keys(parsed).filter((locale) => Boolean(parsed[locale]));
  }
  if (!Array.isArray(parsed)) return [...SUPPORTED_MENU_LANGUAGES];
  const selected = new Set(parsed.map((locale) => String(locale).toLowerCase()));
  const defaultLanguage = SUPPORTED_MENU_LANGUAGES.includes(menu?.default_language)
    ? menu.default_language : 'en';
  selected.add(defaultLanguage);
  return SUPPORTED_MENU_LANGUAGES.filter((locale) => selected.has(locale));
};

const getEngagement = (
  product,
) =>
  product?.engagement ||
  {};
function PublicMenu() {
  const { establishmentSlug, menuSlug } =
    useParams();
  const menuBasePath = `/${encodeURIComponent(establishmentSlug || '')}/${encodeURIComponent(menuSlug || '')}`;
  const menuStorageId = `${establishmentSlug || ''}__${menuSlug || ''}`;
  const navigate =
    useNavigate();
  const {
    t,
    language,
    changeLanguage,
  } = useLanguage();
  const [
    menuData,
    setMenuData,
  ] = useState(null);
  const [
    cartItems,
    setCartItems,
  ] = useState([]);
  const [
    likedProductIds,
    setLikedProductIds,
  ] = useState([]);
  const [
    loading,
    setLoading,
  ] = useState(true);
  const [
    loadError,
    setLoadError,
  ] = useState(false);
  const menu =
    menuData?.menu ||
    null;
  const establishment =
    menuData?.establishment ||
    null;
  const enabledLanguages = useMemo(() => getMenuLanguages(menu), [menu]);
  const defaultMenuLanguage = SUPPORTED_MENU_LANGUAGES.includes(menu?.default_language)
    ? menu.default_language : 'en';
  const selectedLanguage = enabledLanguages.includes(language)
    ? language : (enabledLanguages.includes(defaultMenuLanguage) ? defaultMenuLanguage : enabledLanguages[0] || 'en');
  const template =
    menuData?.template ||
    menu?.template ||
    menu?.menu_template ||
    null;
  const categories =
    toArray(
      menuData?.categories,
    );
  const products =
    toArray(
      menuData?.products,
    );
  const featuredProducts =
    useMemo(() => {
      const apiFeatured =
        toArray(
          menuData?.featured_products,
        );
      if (
        apiFeatured.length >
        0
      ) {
        return apiFeatured;
      }
      return products
        .filter(
          (product) =>
            product
              ?.is_featured !==
            false,
        )
        .slice(
          0,
          10,
        );
    }, [
      menuData
        ?.featured_products,
      products,
    ]);
  const orderingEnabled =
    Boolean(
      menu?.ordering_enabled ??
        menu?.settings
          ?.ordering_enabled ??
        establishment
          ?.ordering_enabled ??
        false,
    );
  const cartStorageKey =
    `${PUBLIC_MENU_CART_PREFIX}${menuStorageId}`;
  const likesStorageKey =
    `${PUBLIC_MENU_LIKES_PREFIX}${menuStorageId}`;
  const languageStorageKey =
    `${PUBLIC_MENU_LANGUAGE_PREFIX}${menuStorageId}`;
  const cartCount =
    cartItems.reduce(
      (
        total,
        item,
      ) =>
        total +
        Number(
          item?.quantity ||
            0,
        ),
      0,
    );
  const cartTotal =
    cartItems.reduce(
      (
        total,
        item,
      ) =>
        total +
        Number(
          item?.total_price ||
            0,
        ),
      0,
    );
  const fetchPublicMenu =
    async () => {
      try {
        setLoading(
          true,
        );
        setLoadError(
          false,
        );
        const response =
          await publicMenuApi.getMenu(
            establishmentSlug,
            menuSlug,
          );
        const nextMenuData =
          response.data?.data ||
          null;
        if (
          !nextMenuData ||
          nextMenuData
            ?.menu
            ?.is_active ===
            false
        ) {
          throw new Error(
            'Menu unavailable.',
          );
        }
        setMenuData(
          nextMenuData,
        );
        /*
         * Real public analytics:
         * count the actual public menu visit.
         */
        try {
          await publicMenuApi.track(
            establishmentSlug,
            menuSlug,
            {
              type:
                'menu_view',
              menu_id:
                nextMenuData?.menu?.id ||
                undefined,
            },
          );
        } catch {
          // Analytics must never block the public menu.
        }
      } catch {
        setLoadError(
          true,
        );
      } finally {
        setLoading(
          false,
        );
      }
    };
  useEffect(() => {

    try {
      setCartItems(
        JSON.parse(
          localStorage.getItem(
            cartStorageKey,
          ) ||
            '[]',
        ),
      );
    } catch {
      setCartItems([]);
    }
    try {
      setLikedProductIds(
        JSON.parse(
          localStorage.getItem(
            likesStorageKey,
          ) ||
            '[]',
        ),
      );
    } catch {
      setLikedProductIds(
        [],
      );
    }
  }, [
    cartStorageKey,
    changeLanguage,
    languageStorageKey,
    likesStorageKey,
  ]);
  useEffect(() => {
    fetchPublicMenu();
  }, [establishmentSlug, menuSlug]);
  useEffect(() => {
    localStorage.setItem(
      cartStorageKey,
      JSON.stringify(
        cartItems,
      ),
    );
  }, [
    cartItems,
    cartStorageKey,
  ]);
  useEffect(() => {
    localStorage.setItem(
      likesStorageKey,
      JSON.stringify(
        likedProductIds,
      ),
    );
  }, [
    likedProductIds,
    likesStorageKey,
  ]);
  useEffect(() => {
    if (!menu) return;
    const savedLanguage = localStorage.getItem(languageStorageKey);
    const nextLanguage = savedLanguage && enabledLanguages.includes(savedLanguage)
      ? savedLanguage
      : (enabledLanguages.includes(defaultMenuLanguage) ? defaultMenuLanguage : enabledLanguages[0] || 'en');
    if (language !== nextLanguage) changeLanguage(nextLanguage);
    if (savedLanguage && savedLanguage !== nextLanguage) {
      localStorage.setItem(languageStorageKey, nextLanguage);
    }
  }, [menu, languageStorageKey, enabledLanguages, defaultMenuLanguage, changeLanguage]);
  const handleLanguageChange =
    (
      languageCode,
    ) => {
      if (!enabledLanguages.includes(languageCode)) return;
      changeLanguage(
        languageCode,
      );
      localStorage.setItem(
        languageStorageKey,
        languageCode,
      );
    };
  /*
   * IMPORTANT:
   * PublicMenuExperience calls this every time a product
   * card/featured product is opened.
   *
   * This is the missing bridge that makes product_view
   * analytics increase on the REAL public menu only.
   */
  const trackProductView =
    async (
      product,
    ) => {
      if (
        !product?.id
      ) {
        return;
      }
      try {
        await publicMenuApi.track(
          establishmentSlug,
          menuSlug,
          {
            type:
              'product_view',
            menu_id:
              menu?.id ||
              undefined,
            product_id:
              Number(
                product.id,
              ),
            category_id:
              product
                ?.category_id
                ? Number(
                    product.category_id,
                  )
                : null,
          },
        );
        /*
         * Optimistically update the value already displayed
         * in the public experience without refetching the menu.
         */
        setMenuData(
          (
            currentData,
          ) => {
            if (
              !currentData
            ) {
              return currentData;
            }
            const updateProduct =
              (
                currentProduct,
              ) => {
                if (
                  Number(
                    currentProduct
                      ?.id,
                  ) !==
                  Number(
                    product.id,
                  )
                ) {
                  return currentProduct;
                }
                const engagement =
                  getEngagement(
                    currentProduct,
                  );
                return {
                  ...currentProduct,
                  engagement: {
                    ...engagement,
                    visits_count:
                      Number(
                        engagement
                          ?.visits_count ||
                          0,
                      ) +
                      1,
                  },
                };
              };
            const updateCategory =
              (
                category,
              ) => ({
                ...category,
                products:
                  toArray(
                    category
                      ?.products,
                  ).map(
                    updateProduct,
                  ),
                children:
                  toArray(
                    category
                      ?.children,
                  ).map(
                    (
                      child,
                    ) => ({
                      ...child,
                      products:
                        toArray(
                          child
                            ?.products,
                        ).map(
                          updateProduct,
                        ),
                    }),
                  ),
              });
            return {
              ...currentData,
              products:
                toArray(
                  currentData
                    ?.products,
                ).map(
                  updateProduct,
                ),
              featured_products:
                toArray(
                  currentData
                    ?.featured_products,
                ).map(
                  updateProduct,
                ),
              categories:
                toArray(
                  currentData
                    ?.categories,
                ).map(
                  updateCategory,
                ),
            };
          },
        );
      } catch (
        error
      ) {
        /*
         * Keep the public menu usable, but log this so a
         * failing analytics route is visible during development.
         */
        console.error(
          'Failed to track product view:',
          error,
        );
      }
    };
  const trackCategoryView =
    async (
      categoryId,
    ) => {
      if (
        !categoryId
      ) {
        return;
      }
      try {
        await publicMenuApi.track(
          establishmentSlug,
          menuSlug,
          {
            type:
              'category_view',
            menu_id:
              menu?.id ||
              undefined,
            category_id:
              Number(
                categoryId,
              ),
          },
        );
      } catch (
        error
      ) {
        console.error(
          'Failed to track category view:',
          error,
        );
      }
    };
  const updateProductEngagement =
    (
      productId,
      updater,
    ) => {
      setMenuData(
        (
          currentData,
        ) => {
          if (
            !currentData
          ) {
            return currentData;
          }
          const updateProduct =
            (
              product,
            ) => {
              if (
                Number(
                  product?.id,
                ) !==
                Number(
                  productId,
                )
              ) {
                return product;
              }
              return {
                ...product,
                engagement:
                  updater(
                    getEngagement(
                      product,
                    ),
                  ),
              };
            };
          const updateCategory =
            (
              category,
            ) => ({
              ...category,
              products:
                toArray(
                  category
                    ?.products,
                ).map(
                  updateProduct,
                ),
              children:
                toArray(
                  category
                    ?.children,
                ).map(
                  (
                    child,
                  ) => ({
                    ...child,
                    products:
                      toArray(
                        child
                          ?.products,
                      ).map(
                        updateProduct,
                      ),
                  }),
                ),
            });
          return {
            ...currentData,
            products:
              toArray(
                currentData
                  ?.products,
              ).map(
                updateProduct,
              ),
            categories:
              toArray(
                currentData
                  ?.categories,
              ).map(
                updateCategory,
              ),
            featured_products:
              toArray(
                currentData
                  ?.featured_products,
              ).map(
                updateProduct,
              ),
          };
        },
      );
    };
  const toggleLike =
    async (
      productId,
    ) => {
      const numericId =
        Number(
          productId,
        );
      const alreadyLiked =
        likedProductIds.includes(
          numericId,
        );
      setLikedProductIds(
        (
          currentIds,
        ) =>
          alreadyLiked
            ? currentIds.filter(
                (id) =>
                  Number(
                    id,
                  ) !==
                  numericId,
              )
            : [
                ...currentIds,
                numericId,
              ],
      );
      updateProductEngagement(
        numericId,
        (
          engagement,
        ) => ({
          ...engagement,
          likes_count:
            Math.max(
              0,
              Number(
                engagement
                  ?.likes_count ||
                  0,
              ) +
                (alreadyLiked
                  ? -1
                  : 1),
            ),
        }),
      );
      try {
        await publicMenuApi.toggleLike(
          establishmentSlug,
          menuSlug,
          numericId,
        );
      } catch {
        // Keep optimistic UI.
      }
    };
  const createReview =
    async (
      productId,
      payload,
    ) => {
      await publicMenuApi.createReview(
        productId,
        payload,
      );
    };
  const handleOrderNow =
    (
      item,
    ) => {
      if (
        !orderingEnabled
      ) {
        return;
      }
      setCartItems(
        (
          currentItems,
        ) => {
          const nextItems = [
            ...currentItems,
            item,
          ];
          localStorage.setItem(
            cartStorageKey,
            JSON.stringify(
              nextItems,
            ),
          );
          return nextItems;
        },
      );
      window.dispatchEvent(
        new Event(
          'public-menu-cart-updated',
        ),
      );
      navigate(
        `${menuBasePath}/order`,
      );
    };
  if (
    loading
  ) {
    return (
      <main className="public-menu public-menu--loading">
        <Loader2
          size={38}
        />
        <h1>
          {t(
            'publicMenu.loading',
          )}
        </h1>
      </main>
    );
  }
  if (
    loadError ||
    !menuData
  ) {
    return (
      <main className="public-menu public-menu--loading">
        <UtensilsCrossed
          size={42}
        />
        <h1>
          {t(
            'publicMenu.notFoundTitle',
          )}
        </h1>
        <p>
          {t(
            'publicMenu.notFoundDescription',
          )}
        </p>
      </main>
    );
  }
  return (
    <PublicMenuExperience
      mode="public"
      menu={{ ...menu, enabled_languages: enabledLanguages }}
      establishment={
        establishment
      }
      categories={
        categories
      }
      products={
        products
      }
      featuredProducts={
        featuredProducts
      }
      template={
        template
      }
      designSettings={{
        ...(
          menuData
            ?.design_settings ||
          {}
        ),
        ...(
          menu
            ?.design_settings ||
          {}
        ),
        ...(
          menu
            ?.menu_design_settings ||
          {}
        ),
      }}
      orderingEnabled={
        orderingEnabled
      }
      language={selectedLanguage}
      onLanguageChange={
        handleLanguageChange
      }
      likedProductIds={
        likedProductIds
      }
      cartCount={
        cartCount
      }
      cartTotal={
        cartTotal
      }
      /*
       * This is the actual fix.
       * The shared renderer already calls onProductView when
       * opening any product; the real public page must provide it.
       */
      onProductView={
        trackProductView
      }
      onCategoryChange={
        trackCategoryView
      }
      onCheckoutClick={() =>
        navigate(
          `${menuBasePath}/order`,
        )
      }
      onOrdersClick={() =>
        navigate(
          `${menuBasePath}/orders`,
        )
      }
      onLike={
        toggleLike
      }
      onReview={
        createReview
      }
      onOrderNow={
        handleOrderNow
      }
      detailsUrlForProduct={(
        product,
      ) =>
        `${menuBasePath}/products/${
          product?.slug ||
          product?.id
        }`
      }
      showFloatingCart={
        orderingEnabled
      }
    />
  );
}
export default PublicMenu;
